/**
 * Low-level analytics dispatch hook for Direct Sharing feature.
 *
 * Provides type-safe track functions for each sharing event.
 * Higher-level hooks (useShareModalTracking, useShareSearchTracking, etc.)
 * compose these into semantic callbacks for components.
 */
import { useCallback, useMemo } from 'react';

import {
  type SharingAnalyticsEvent,
  type TrackSharingEvent,
} from './sharing-analytics-events';

/**
 * Factory: creates a stable, memoized track function for a given event name.
 *
 * `blockAmplitude` opts the event out of Amplitude (Snowplow + Datadog still
 * fire). New events MUST set this per the Snowplow-first routing policy —
 * Amplitude is ON by default in the v2 pipeline. Existing sharing events are
 * grandfathered and keep their Amplitude stream.
 */
function useTrackEvent<E extends SharingAnalyticsEvent['event_name']>(
  onTrack: TrackSharingEvent,
  eventName: E,
  blockAmplitude = false
) {
  return useCallback(
    (props: Extract<SharingAnalyticsEvent, { event_name: E }>['props']) => {
      onTrack({
        event_name: eventName,
        props,
        options: blockAmplitude ? { posthog: true, amplitude: false } : { posthog: true },
      } as Extract<SharingAnalyticsEvent, { event_name: E }>);
    },
    [onTrack, eventName, blockAmplitude]
  );
}

export function useSharingAnalytics(onTrack: TrackSharingEvent) {
  const trackShareButtonClicked = useTrackEvent(onTrack, 'share_button_clicked');
  const trackShareModalOpened = useTrackEvent(onTrack, 'share_modal_opened');
  const trackShareMemberSearched = useTrackEvent(onTrack, 'share_member_searched');
  const trackShareMemberAdded = useTrackEvent(onTrack, 'share_member_added');
  const trackShareLinkGenerated = useTrackEvent(onTrack, 'share_link_generated');
  const trackShareLinkCopied = useTrackEvent(onTrack, 'share_link_copied');
  const trackShareSaved = useTrackEvent(onTrack, 'share_saved');
  const trackShareAccessChanged = useTrackEvent(onTrack, 'share_access_changed');
  const trackShareAccessRevoked = useTrackEvent(onTrack, 'share_access_revoked');
  const trackShareEditRequested = useTrackEvent(onTrack, 'share_edit_requested');
  const trackShareNotificationReceived = useTrackEvent(
    onTrack,
    'share_notification_received'
  );
  const trackShareNotificationClicked = useTrackEvent(
    onTrack,
    'share_notification_clicked'
  );
  const trackShareExternalConfirmed = useTrackEvent(onTrack, 'share_external_confirmed');
  const trackShareExternalEmailAdded = useTrackEvent(
    onTrack,
    'share_external_email_added'
  );
  const trackShareExternalEmailRemoved = useTrackEvent(
    onTrack,
    'share_external_email_removed'
  );
  const trackShareExternalInlineOpened = useTrackEvent(
    onTrack,
    'share_external_inline_opened'
  );
  const trackShareExternalInlineCancelled = useTrackEvent(
    onTrack,
    'share_external_inline_cancelled'
  );
  // New events — Snowplow-first: opt out of Amplitude (the dangerous default).
  const trackShareExternalRevealOpened = useTrackEvent(
    onTrack,
    'share_external_reveal_opened',
    true
  );
  const trackShareExternalLinkSendSubmitted = useTrackEvent(
    onTrack,
    'share_external_link_send_submitted',
    true
  );
  const trackSessionLinkShareOtpVerified = useTrackEvent(
    onTrack,
    'session_link_share_otp_verified'
  );
  const trackSessionLinkShareOtpFailed = useTrackEvent(
    onTrack,
    'session_link_share_otp_failed'
  );

  return useMemo(
    () => ({
      trackShareButtonClicked,
      trackShareModalOpened,
      trackShareMemberSearched,
      trackShareMemberAdded,
      trackShareLinkGenerated,
      trackShareLinkCopied,
      trackShareSaved,
      trackShareAccessChanged,
      trackShareAccessRevoked,
      trackShareEditRequested,
      trackShareNotificationReceived,
      trackShareNotificationClicked,
      trackShareExternalConfirmed,
      trackShareExternalEmailAdded,
      trackShareExternalEmailRemoved,
      trackShareExternalInlineOpened,
      trackShareExternalInlineCancelled,
      trackShareExternalRevealOpened,
      trackShareExternalLinkSendSubmitted,
      trackSessionLinkShareOtpVerified,
      trackSessionLinkShareOtpFailed,
    }),
    [
      trackShareButtonClicked,
      trackShareModalOpened,
      trackShareMemberSearched,
      trackShareMemberAdded,
      trackShareLinkGenerated,
      trackShareLinkCopied,
      trackShareSaved,
      trackShareAccessChanged,
      trackShareAccessRevoked,
      trackShareEditRequested,
      trackShareNotificationReceived,
      trackShareNotificationClicked,
      trackShareExternalConfirmed,
      trackShareExternalEmailAdded,
      trackShareExternalEmailRemoved,
      trackShareExternalInlineOpened,
      trackShareExternalInlineCancelled,
      trackShareExternalRevealOpened,
      trackShareExternalLinkSendSubmitted,
      trackSessionLinkShareOtpVerified,
      trackSessionLinkShareOtpFailed,
    ]
  );
}
